import { Button } from '@components/common/Button';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { ISelectOption } from '@components/common/Select/types';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { IAddAddressProps, IAddressDetails, IAddressForm } from './types';
import { useAddressesByPostcode } from '@queries/useAddressByPostcode/useAddressesByPostcode';
import { IAddressResponseDetails } from '@app-types/address';
import { ManualAddressAddition } from './FormsComponents/ManualAddressAddition';
import { FullAddressAddition } from './FormsComponents/FullAddressAddition';
import { FC, useEffect, useState } from 'react';
import { useDatesOptions } from '@hooks/useDatesOptions';
import { v4 as uuidv4 } from 'uuid';
import { addAddressQuestions } from './questions';

export const addressToOptionTransformer = (address: IAddressResponseDetails): ISelectOption => {
  const addressValue = address.fullAddress;

  return { label: addressValue, value: addressValue };
};

export const AddAddress: FC<IAddAddressProps> = ({ onSubmitAddress, addressToEdit, onGoBack }) => {
  const [isManual, setIsManual] = useState(Boolean(addressToEdit?.postcode && !addressToEdit?.fullAddress));
  const intl = useIntl();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IAddressForm>({
    defaultValues: {
      postcode: addressToEdit?.postcode || '',
      address1: addressToEdit?.address1 || '',
      address2: addressToEdit?.address2 || '',
      fullAddress: addressToEdit?.fullAddress || null,
      city: addressToEdit?.city || '',
      country: addressToEdit?.country || '',
      month: addressToEdit?.month || null,
      year: addressToEdit?.year || null,
    },
  });

  const formState = form.watch();
  const isNextStepDisabled = Object.values(form.formState.errors).length > 0;
  const postcode = formState.postcode || '';
  const { data: addresses = [], refetch: fetchAddressesByPostcode } = useAddressesByPostcode(
    addressToEdit?.postcode || postcode,
  );
  const addressOptions = addresses.map(addressToOptionTransformer);
  const { monthOptions, generateLastXYearsOptions } = useDatesOptions();
  const yearOptions = generateLastXYearsOptions(50);

  useEffect(() => {
    if (addressOptions.length && !formState.fullAddress && !isManual) {
      form.setValue('fullAddress', String(addressOptions[0].value));
    }
  }, [addressOptions]);

  useEffect(() => {
    if (isManual || !formState.fullAddress || !addresses.length) {
      return;
    }

    const selected = addresses.find(a => a.fullAddress === formState.fullAddress);
    if (selected) {
      form.setValue('address1', selected.addressLine1 ?? '');
      form.setValue('address2', selected.addressLine2 ?? '');
      form.setValue('city', selected.townOrCity ?? '');
      form.setValue('country', selected.country ?? '');
    }
  }, [formState.fullAddress, isManual, addresses]);

  const submitFormHandler = (addressForm: IAddressForm) => {
    const address = {
      id: addressToEdit?.id || uuidv4(),
      ...addressForm,
    } as IAddressDetails;

    onSubmitAddress(address);
  };

  const setManualHandler = () => {
    setIsManual(true);
    form.resetField('fullAddress');
    form.clearErrors();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage id={'applicationForm.ADD_ADDRESS.whatIsCurrentAddress.label'} tagName={'h4'} />

          <div>
            <FormattedMessage id={addAddressQuestions.postcode.labelKey} tagName={'h4'} />
            <div className='flex items-center justify-between'>
              <FormTextField name={'postcode'} rules={{ required: requiredFieldErrorMessage }} />

              {!isManual && (
                <div>
                  <Button
                    className={'ml-5 whitespace-nowrap'}
                    variant={'outlined'}
                    disabled={!postcode?.length}
                    onClick={() => fetchAddressesByPostcode()}
                  >
                    <FormattedMessage id={'applicationForm.ADD_ADDRESS.findAddress'} />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {isManual ? (
            <ManualAddressAddition requiredFieldErrorMessage={requiredFieldErrorMessage} />
          ) : (
            <FullAddressAddition
              requiredFieldErrorMessage={requiredFieldErrorMessage}
              isManual={isManual}
              value={formState.fullAddress}
              addressOptions={addressOptions}
              onLinkClick={setManualHandler}
            />
          )}

          <div className={'mt-8'}>
            <FormattedMessage id={addAddressQuestions.whenMoveDate.labelKey} tagName={'h4'} />
            <div className={'flex justify-between'}>
              <div className={'mr-2 w-1/2'}>
                <FormattedMessage id={'common.month'} tagName={'p'} />
                <FormSelect
                  name={'month'}
                  rules={{ required: requiredFieldErrorMessage }}
                  options={monthOptions}
                  selectProps={{
                    isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                      option.value === selectedOptionValue,
                    value: formState.month,
                  }}
                  selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.selectMonth' }) }}
                  className={'w-full'}
                />
              </div>
              <div className={'ml-2 w-1/2'}>
                <FormattedMessage id={'common.year'} tagName={'p'} />
                <FormSelect
                  name={'year'}
                  rules={{ required: requiredFieldErrorMessage }}
                  options={yearOptions}
                  selectProps={{
                    isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                      option.value === selectedOptionValue,
                    value: formState.year,
                  }}
                  selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.selectYear' }) }}
                  className={'w-full'}
                />
              </div>
            </div>
          </div>

          <div className={'mt-8 flex justify-between'}>
            <Button variant={'outlined'} onClick={onGoBack}>
              <FormattedMessage id={'cta.back'} />
            </Button>

            <Button variant={'contained'} type={'submit'} disabled={isNextStepDisabled}>
              <FormattedMessage id={'cta.continue'} />
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
